import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Calendar,
  Clock,
  User,
  Building,
  Target,
  Flag,
  MapPin,
  FileText,
  Users,
  ArrowRight,
  Edit,
  Trash2,
  CheckCircle,
  AlertCircle,
  Plus,
  Eye,
  RefreshCw,
  Link,
  UserCheck,
  Activity,
  Timer,
  MessageSquare,
  TrendingUp,
  Download
} from 'lucide-react';
import entityRelationService from '../../services/entityRelationService';
import { useNavigate } from 'react-router-dom';

const ActivityDetail = ({ activity, onClose, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const safeT = (key, fallback) => t(key, fallback);
  const navigate = useNavigate();
  const [childActivities, setChildActivities] = useState([]);
  const [relatedEntities, setRelatedEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // Load activity details and child activities
  useEffect(() => {
    if (!activity || !activity.ActivityID) return;
    let cancelled = false;

    (async () => {
      await loadActivityDetails(cancelled);
      if (!cancelled) await loadRelatedEntities();
    })();

    return () => {
      cancelled = true;
    };
  }, [activity?.ActivityID]);

  const loadActivityDetails = async (cancelled = false) => {
    if (cancelled) return;
    setLoading(true);
    setError(null);

    try {
      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch(`/api/activities/${activity.ActivityID}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load activity details');
      }

      const data = await response.json();
      if (cancelled) return;
      const info = data.activity || data;
      setChildActivities(info?.childActivities || []);

    } catch (err) {
      if (!cancelled) setError(err.message);
    } finally {
      if (!cancelled) setLoading(false);
    }
  };

  const loadRelatedEntities = async () => {
    if (!activity || !activity.ActivityID) return;
    
    try {
      const relations = await entityRelationService.getRelatedEntities(
        'Activity',
        activity.ActivityID,
        { direction: 'both' }
      );
      
      // Transform relations to get related entities (not Activity itself)
      const entities = (relations.relations || []).map(rel => {
        const entityType = rel.SourceEntityType === 'Activity' 
          ? rel.TargetEntityType 
          : rel.SourceEntityType;
        const entityId = rel.SourceEntityType === 'Activity' 
          ? rel.TargetEntityID 
          : rel.SourceEntityID;
        
        // Get entity name from relation data if available
        let entityName = '';
        switch (entityType) {
          case 'Customer':
            entityName = rel.CustomerName || `${rel.FirstName || ''} ${rel.LastName || ''}`.trim() || rel.CompanyName || `Customer ${entityId}`;
            break;
          case 'Lead':
            entityName = rel.Title || rel.ContactName || rel.CompanyName || `Lead ${entityId}`;
            break;
          case 'Opportunity':
            entityName = rel.OpportunityName || rel.Title || `Opportunity ${entityId}`;
            break;
          case 'SaleNote': {
            const noteFirst = (rel.NoteText ? String(rel.NoteText).split('\n')[0] : '').trim();
            entityName = noteFirst || `Sale Note ${entityId}`;
            break;
          }
          case 'Contract':
            entityName = rel.ContractName || `Contract ${entityId}`;
            break;
          case 'Invoice':
            entityName = rel.InvoiceNumber || `Invoice ${entityId}`;
            break;
          case 'SupportTicket':
            entityName = rel.TicketNumber || rel.Subject || `Ticket ${entityId}`;
            break;
          default:
            entityName = `${entityType} ${entityId}`;
        }
        
        return {
          EntityType: entityType,
          EntityID: entityId,
          EntityName: entityName,
          RelationID: rel.RelationID,
          RelationType: rel.RelationType,
          // Store CustomerType if available in relation data
          CustomerType: entityType === 'Customer' ? (rel.CustomerType || null) : undefined
        };
      });

      // Enrich all entities - especially Customer entities to get CustomerType
      const enriched = await Promise.all(entities.map(async (e) => {
        try {
          // For Customer entities, always enrich to get CustomerType if not already available
          if (e.EntityType === 'Customer' && !e.CustomerType) {
            try {
              const resp = await fetch(`/api/customers/${e.EntityID}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
              if (resp.ok) {
                const data = await resp.json();
                const cust = data?.customer || data || {};
                const customerType = cust.CustomerType || 'Company';
                const name = customerType === 'Person'
                  ? `${cust.FirstName || ''} ${cust.LastName || ''}`.trim() || cust.CustomerName
                  : cust.CompanyName || cust.CustomerName;
                return { ...e, EntityName: name || e.EntityName, CustomerType: customerType };
              }
            } catch (err) {
              console.warn('⚠️ [ActivityDetail] Failed to enrich Customer relation:', err);
              return { ...e, CustomerType: 'Company' };
            }
          }
          
          // Only enrich if we don't have a good name
          const defaultLike = !e.EntityName || e.EntityName === `${e.EntityType} ${e.EntityID}`;
          if (!defaultLike) return e;
          
          if (e.EntityType === 'Customer') {
            const resp = await fetch(`/api/customers/${e.EntityID}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            if (resp.ok) {
              const data = await resp.json();
              const cust = data?.customer || data || {};
              const customerType = cust.CustomerType || e.CustomerType || 'Company';
              const name = customerType === 'Person'
                ? `${cust.FirstName || ''} ${cust.LastName || ''}`.trim() || cust.CustomerName
                : cust.CompanyName || cust.CustomerName;
              return { ...e, EntityName: name || e.EntityName, CustomerType: customerType };
            }
          } else if (e.EntityType === 'Lead') {
            const selectedRole = localStorage.getItem('selectedRole');
            const resp = await fetch(`/api/leads/${e.EntityID}`, { 
              headers: { 
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
              } 
            });
            if (resp.ok) {
              const data = await resp.json();
              const ld = data?.lead || data || {};
              const name = ld.Title || ld.ContactName || ld.CompanyName;
              return { ...e, EntityName: name || e.EntityName };
            }
          } else if (e.EntityType === 'Opportunity') {
            const selectedRole = localStorage.getItem('selectedRole');
            const resp = await fetch(`/api/opportunities/${e.EntityID}`, { 
              headers: { 
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
              } 
            });
            if (resp.ok) {
              const data = await resp.json();
              const opp = data?.opportunity || data || {};
              const name = opp.OpportunityName || opp.CustomerName;
              return { ...e, EntityName: name || e.EntityName };
            }
          } else if (e.EntityType === 'SaleNote') {
            const resp = await fetch(`/api/sales-notes/${e.EntityID}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            if (resp.ok) {
              const data = await resp.json();
              const sn = data?.saleNote || data || {};
              const firstLine = (sn.NoteText ? String(sn.NoteText).split('\n')[0] : '').trim();
              return { ...e, EntityName: firstLine || sn.Title || e.EntityName };
            }
          }
        } catch (_) { /* ignore */ }
        return e;
      }));

      setRelatedEntities(enriched);
    } catch (err) {
      console.error('Failed to load related entities:', err);
      // Fallback to empty array
      setRelatedEntities([]);
    }
  };

  // Check if user has Google Calendar connected
  useEffect(() => {
    const checkGoogleCalendarConnection = async () => {
      try {
        const response = await fetch('/api/google-calendar/settings', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setGoogleCalendarConnected(data.connected && data.settings?.isEnabled);
        }
      } catch (err) {
        console.error('Error checking Google Calendar connection:', err);
      }
    };
    
    checkGoogleCalendarConnection();
  }, []);

  const handleExportToCalendar = async () => {
    try {
      const queryParams = new URLSearchParams({
        activityIds: activity.ActivityID.toString()
      });

      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch(`/api/activities/export/calendar?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to export activity');
      }

      // Get the blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-${activity.ActivityID}-${new Date().toISOString().split('T')[0]}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert(err.message || 'Failed to export activity');
    }
  };

  const handleSyncWithGoogle = async () => {
    if (syncing) return;
    
    try {
      setSyncing(true);
      const response = await fetch('/api/google-calendar/sync/now', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to sync with Google Calendar');
      }

      const data = await response.json();
      alert(`Sync completed!\nExported: ${data.exported}\nImported: ${data.imported}`);
      
      // Reload activity details to get updated sync status
      await loadActivityDetails();
    } catch (err) {
      console.error('Sync error:', err);
      alert(err.message || 'Failed to sync with Google Calendar');
    } finally {
      setSyncing(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Done': return 'text-green-600 bg-green-100';
      case 'In Progress': return 'text-blue-600 bg-blue-100';
      case 'Planned': return 'text-gray-600 bg-gray-100';
      case 'On Hold': return 'text-yellow-600 bg-yellow-100';
      case 'Cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'text-red-600';
      case 'High': return 'text-orange-600';
      case 'Medium': return 'text-blue-600';
      case 'Low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  // Get priority icon
  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'Critical': return <Flag className="h-4 w-4" fill="currentColor" />;
      case 'High': return <Flag className="h-4 w-4" fill="currentColor" />;
      case 'Medium': return <Flag className="h-4 w-4" />;
      case 'Low': return <Flag className="h-4 w-4" />;
      default: return <Flag className="h-4 w-4" />;
    }
  };

  // Check if activity is overdue
  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === 'Done' || status === 'Cancelled') return false;
    return new Date(dueDate) < new Date();
  };

  // Helpers for entity relations UI (icons, paths, and colors)
  const getEntityColors = (entityType, customerType = null) => {
    // For Customer entities, use different colors based on CustomerType
    if (entityType === 'Customer' && customerType) {
      if (customerType === 'Person') {
        // Customer (Person) = blue
        return {
          bg: 'bg-blue-50 dark:bg-blue-900/20',
          border: 'border-blue-200 dark:border-blue-800',
          hoverBg: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
          icon: 'text-blue-600 dark:text-blue-400',
          badge: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
        };
      } else {
        // Customer (Company) = green
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          hoverBg: 'hover:bg-green-100 dark:hover:bg-green-900/30',
          icon: 'text-green-600 dark:text-green-400',
          badge: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
        };
      }
    }
    
    switch (entityType) {
      case 'Customer':
        // Default Customer (fallback) = green (Company)
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          hoverBg: 'hover:bg-green-100 dark:hover:bg-green-900/30',
          icon: 'text-green-600 dark:text-green-400',
          badge: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
        };
      case 'Lead':
        // Lead = red
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          hoverBg: 'hover:bg-red-100 dark:hover:bg-red-900/30',
          icon: 'text-red-600 dark:text-red-400',
          badge: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
        };
      case 'Opportunity':
        // Opportunity = violet
        return {
          bg: 'bg-violet-50 dark:bg-violet-900/20',
          border: 'border-violet-200 dark:border-violet-800',
          hoverBg: 'hover:bg-violet-100 dark:hover:bg-violet-900/30',
          icon: 'text-violet-600 dark:text-violet-400',
          badge: 'bg-violet-100 dark:bg-violet-900 text-violet-800 dark:text-violet-200'
        };
      case 'Activity':
        return {
          bg: 'bg-orange-50 dark:bg-orange-900/20',
          border: 'border-orange-200 dark:border-orange-800',
          hoverBg: 'hover:bg-orange-100 dark:hover:bg-orange-900/30',
          icon: 'text-orange-600 dark:text-orange-400',
          badge: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
        };
      case 'SaleNote':
        // SaleNote = yellow
        return {
          bg: 'bg-yellow-50 dark:bg-yellow-900/20',
          border: 'border-yellow-200 dark:border-yellow-800',
          hoverBg: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/30',
          icon: 'text-yellow-600 dark:text-yellow-400',
          badge: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
        };
      default:
        return {
          bg: 'bg-gray-50 dark:bg-gray-700',
          border: 'border-gray-200 dark:border-gray-700',
          hoverBg: 'hover:bg-gray-100 dark:hover:bg-gray-600',
          icon: 'text-gray-600 dark:text-gray-400',
          badge: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
        };
    }
  };

  // Get entity icon
  const getEntityIcon = (entityType) => {
    const colors = getEntityColors(entityType);
    const iconClass = `h-4 w-4 ${colors.icon}`;
    
    switch (entityType) {
      case 'Customer': return <Building className={iconClass} />;
      case 'Lead': return <User className={iconClass} />;
      case 'Opportunity': return <Target className={iconClass} />;
      case 'SaleNote': return <FileText className={iconClass} />;
      case 'Contract': return <FileText className={iconClass} />;
      case 'Invoice': return <FileText className={iconClass} />;
      case 'SupportTicket': return <AlertCircle className={iconClass} />;
      case 'Activity': return <Activity className={iconClass} />;
      default: return <FileText className={iconClass} />;
    }
  };

  // Get navigation path for entity type
  const getEntityPath = (entityType, entityId) => {
    switch (entityType) {
      case 'Customer': return `/customers/${entityId}`;
      case 'Lead': return `/leads/${entityId}`;
      case 'Opportunity': return `/opportunities/${entityId}`;
      case 'SaleNote': return `/notes?noteId=${entityId}`;
      case 'Contract': return `/contracts/${entityId}`;
      case 'Invoice': return `/invoices/${entityId}`;
      case 'SupportTicket': return `/support/${entityId}`;
      case 'Activity': return `/activities/${entityId}`;
      default: return '#';
    }
  };

  // Format duration
  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${remainingMinutes}m`;
  };

  if (!activity) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${getPriorityColor(activity.Priority)} bg-opacity-10`}>
              <Activity className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{activity.Subject}</h2>
                {activity.GoogleCalendarSynced && (
                  <div 
                    className="flex items-center space-x-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-xs font-medium rounded-full border border-blue-200 dark:border-blue-800"
                    title={`Synced with Google Calendar${activity.LastGoogleSyncAt ? ` at ${new Date(activity.LastGoogleSyncAt).toLocaleString()}` : ''}`}
                  >
                    <Calendar className="h-3.5 w-3.5" />
                    <span>Synced</span>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-4 mt-1">
                <span className="text-sm text-gray-500 dark:text-gray-400">{activity.ActivityType}</span>
                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(activity.Status)}`}>
                  {activity.Status}
                </span>
                <div className={`flex items-center space-x-1 ${getPriorityColor(activity.Priority)}`}>
                  {getPriorityIcon(activity.Priority)}
                  <span className="text-sm font-medium">{activity.Priority}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {googleCalendarConnected && (
              <button
                onClick={handleSyncWithGoogle}
                disabled={syncing}
                className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-100 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Sync with Google Calendar"
              >
                <RefreshCw className={`h-5 w-5 ${syncing ? 'animate-spin' : ''}`} />
              </button>
            )}
            <button
              onClick={handleExportToCalendar}
              className="text-green-600 hover:text-green-900 p-2 rounded-lg hover:bg-green-100 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20"
              title="Download as .ics file"
            >
              <Download className="h-5 w-5" />
            </button>
            <button
              onClick={onEdit}
              className="text-gray-600 hover:text-gray-900 p-2 rounded-lg hover:bg-gray-100"
              title="Edit Activity"
            >
              <Edit className="h-5 w-5" />
            </button>
            <button
              onClick={onDelete}
              className="text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-100"
              title="Delete Activity"
            >
              <Trash2 className="h-5 w-5" />
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {/* Overdue Warning */}
          {isOverdue(activity.DueDate, activity.Status) && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700 dark:text-red-300 font-medium">This activity is overdue</span>
            </div>
          )}

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Related Entities - grouped chips similar to ActivityForm */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                  <Link className="h-4 w-4" />
                  <span className="ml-2">Related To</span>
                  {relatedEntities.length > 0 && (
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({relatedEntities.length})</span>
                  )}
                </h3>
                {relatedEntities && relatedEntities.length > 0 ? (
                  <div className="space-y-3">
                    {['Customer','Activity','Lead','Opportunity','SaleNote','Contract','Invoice','SupportTicket'].map(type => {
                      const ofType = relatedEntities.filter(e => e.EntityType === type);
                      if (ofType.length === 0) return null;
                      
                      // For Customer type, sort by CustomerType (Company first, then Person)
                      const sortedEntities = type === 'Customer' 
                        ? [...ofType].sort((a, b) => {
                            const aType = a.CustomerType || 'Company';
                            const bType = b.CustomerType || 'Company';
                            if (aType === bType) return 0;
                            return aType === 'Company' ? -1 : 1;
                          })
                        : ofType;
                      
                      return (
                        <div key={type} className="space-y-2">
                          <div className="flex items-center space-x-2">
                            {getEntityIcon(type)}
                            <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">{type} ({ofType.length})</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {sortedEntities.map((rel, idx) => {
                              const isPersonCustomer = rel.EntityType === 'Customer' && rel.CustomerType === 'Person';
                              const colors = getEntityColors(rel.EntityType, rel.CustomerType);
                              
                              return (
                                <button
                                  key={`${rel.EntityType}-${rel.EntityID}-${idx}`}
                                  type="button"
                                  onClick={() => {
                                    const path = getEntityPath(rel.EntityType, rel.EntityID);
                                    if (path !== '#') { navigate(path); onClose(); }
                                  }}
                                  className={`group flex items-center space-x-2 px-3 py-2 ${colors.bg} ${colors.border} border ${colors.hoverBg} rounded-lg transition-colors ${
                                    isPersonCustomer ? 'ml-6' : ''
                                  }`}
                                >
                                  {getEntityIcon(rel.EntityType)}
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {rel.EntityName || `${rel.EntityType} ${rel.EntityID}`}
                                    {rel.CustomerType && ` (${rel.CustomerType === 'Person' ? 'Individual' : 'Company'})`}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-400">#{rel.EntityID}</span>
                                  <ArrowRight className={`h-3 w-3 ${colors.icon} opacity-0 group-hover:opacity-100`} />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400">
                    <Link className="h-4 w-4" />
                    <p className="text-sm">{safeT('activities.relatedEntities', 'Relationships')}: No items</p>
                  </div>
                )}
                {activity.KanbanStage && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Stage: {activity.KanbanStage}</p>
                )}
              </div>

              {/* Assignment */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                  <Users className="h-4 w-4" />
                  <span className="ml-2">Assignment</span>
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Salesperson:</span>
                    <span className="text-gray-900 dark:text-white">{activity.SalespersonName}</span>
                  </div>
                  {activity.AssignedToName && (
                    <div className="flex items-center space-x-2">
                      <UserCheck className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Assigned to:</span>
                      <span className="text-gray-900 dark:text-white">{activity.AssignedToName}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Parent Activity */}
              {activity.ParentSubject && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                    <ArrowRight className="h-4 w-4" />
                    <span className="ml-2">Parent Activity</span>
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">{activity.ParentSubject}</p>
                </div>
              )}

              {/* Location */}
              {activity.Location && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                    <MapPin className="h-4 w-4" />
                    <span className="ml-2">Location</span>
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300">{activity.Location}</p>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Timeline */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                  <Calendar className="h-4 w-4" />
                  <span className="ml-2">Timeline</span>
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Created:</span>
                    <span className="text-gray-900 dark:text-white">{formatDateTime(activity.CreatedAt)}</span>
                  </div>
                  {activity.DueDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{safeT('activities.dueDateLabel', 'Due Date')}</span>
                      <span className={`text-gray-900 dark:text-white ${isOverdue(activity.DueDate, activity.Status) ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
                        {formatDate(activity.DueDate)}
                      </span>
                    </div>
                  )}
                  {activity.StartDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{safeT('activities.startDateLabel', 'Start Date')}</span>
                      <span className="text-gray-900 dark:text-white">{formatDate(activity.StartDate)}</span>
                    </div>
                  )}
                  {activity.EndDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{safeT('activities.endDateLabel', 'End Date')}</span>
                      <span className="text-gray-900 dark:text-white">{formatDate(activity.EndDate)}</span>
                    </div>
                  )}
                  {activity.FollowUpDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{safeT('activities.followUpLabel', 'Follow-up')}</span>
                      <span className="text-gray-900 dark:text-white">{formatDate(activity.FollowUpDate)}</span>
                    </div>
                  )}
                  {activity.CompletedDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{safeT('activities.completedLabel', 'Completed')}</span>
                      <span className="text-green-600 dark:text-green-400">{formatDateTime(activity.CompletedDate)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Duration */}
              {(activity.EstimatedDuration || activity.ActualDuration) && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                    <Timer className="h-4 w-4" />
                    <span className="ml-2">{safeT('activities.duration', 'Duration')}</span>
                  </h3>
                  <div className="space-y-2 text-sm">
                    {activity.EstimatedDuration && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{safeT('activities.estimated', 'Estimated')}</span>
                        <span className="text-gray-900 dark:text-white">{formatDuration(activity.EstimatedDuration)}</span>
                      </div>
                    )}
                    {activity.ActualDuration && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{safeT('activities.actual', 'Actual')}</span>
                        <span className="text-gray-900 dark:text-white">{formatDuration(activity.ActualDuration)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Progress */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                  <TrendingUp className="h-4 w-4" />
                                      <span className="ml-2">{safeT('activities.progress', 'Progress')}</span>
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Status:</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(activity.Status)}`}>
                      {activity.Status}
                    </span>
                  </div>
                  {activity.Status === 'Done' && (
                    <div className="flex items-center text-green-600 dark:text-green-400 text-sm">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      <span>{safeT('activities.activityCompleted', 'Activity Completed')}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          {activity.Description && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                <FileText className="h-4 w-4" />
                <span className="ml-2">{safeT('activities.description', 'Description')}</span>
              </h3>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{activity.Description}</p>
            </div>
          )}

          {/* Notes */}
          {activity.Notes && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                <MessageSquare className="h-4 w-4" />
                <span className="ml-2">{safeT('activities.notes', 'Notes')}</span>
              </h3>
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{activity.Notes}</p>
            </div>
          )}

          {/* Outcome */}
          {activity.Outcome && (
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-700">
              <h3 className="text-sm font-medium text-green-900 dark:text-green-300 mb-2 flex items-center">
                <CheckCircle className="h-4 w-4" />
                <span className="ml-2">Outcome</span>
              </h3>
              <p className="text-green-800 dark:text-green-200 whitespace-pre-wrap">{activity.Outcome}</p>
            </div>
          )}

          {/* Child Activities */}
          {childActivities.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <ArrowRight className="h-4 w-4" />
                <span className="ml-2">Sub-Activities ({childActivities.length})</span>
              </h3>
              <div className="space-y-3">
                {childActivities.map((child) => (
                  <div
                    key={child.ActivityID}
                    className="bg-white dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full ${
                          child.Status === 'Done' ? 'bg-green-500' :
                          child.Status === 'In Progress' ? 'bg-blue-500' :
                          child.Status === 'Cancelled' ? 'bg-red-500' :
                          'bg-gray-300'
                        }`}></div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{child.Subject}</p>
                          <div className="flex items-center space-x-4 mt-1">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(child.Status)}`}>
                              {child.Status}
                            </span>
                            <div className={`flex items-center space-x-1 ${getPriorityColor(child.Priority)}`}>
                              {getPriorityIcon(child.Priority)}
                              <span className="text-xs">{child.Priority}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        {child.DueDate && (
                          <p className={`text-xs ${
                            isOverdue(child.DueDate, child.Status) ? 'text-red-600' : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            {formatDate(child.DueDate)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Loading Child Activities */}
          {loading && (
            <div className="flex justify-center items-center py-4">
              <RefreshCw className="h-5 w-5 animate-spin text-blue-600 dark:text-blue-400 mr-2" />
              <span className="text-gray-600 dark:text-gray-400">Loading details...</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
            <div>
              Last updated: {formatDateTime(activity.UpdatedAt)}
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={onEdit}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center space-x-1"
              >
                <Edit className="h-4 w-4" />
                <span>Edit Activity</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityDetail; 